import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();

  // Don't show navbar on home page (it has its own navigation)
  const isHomePage = location.pathname === '/';

  // Pages with dark hero sections need light text when not scrolled
  const isDarkHeroPage = ['/contact', '/solutions'].some(path =>
    location.pathname.startsWith(path)
  );

  // Handle scroll effect
  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Portfolio', path: '/portfolio', primary: true },
    { name: 'Solutions', path: '/solutions', primary: false },
    { name: 'Contact', path: '/contact', primary: false },
  ];

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  // Use light colors on dark hero pages when not scrolled
  const useLightColors = isDarkHeroPage && !scrolled;

  // Don't render navbar on home page
  if (isHomePage) {
    return null;
  }

  return (
    <>
      {/* Logo - Fixed top left, matching home page style */}
      <div className="fixed top-6 sm:top-8 left-6 sm:left-8 z-50">
        <Link
          to="/"
          className="group relative flex items-center gap-2 transition-all duration-300"
          title="Go to Home"
        >
          <img
            src="/e8solutionslogo.png"
            alt="E8 Solutions - Home"
            className="h-8 sm:h-10 md:h-12 w-auto transition-all duration-300 group-hover:scale-105"
            style={{
              filter: useLightColors
                ? 'drop-shadow(0 0 8px rgba(255,255,255,0.8)) drop-shadow(0 0 20px rgba(255,255,255,0.4))'
                : scrolled
                  ? 'none'
                  : 'none',
              opacity: scrolled ? 1 : 0.9,
            }}
          />
          {/* Home indicator on hover */}
          <span
            className={`absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap ${
              useLightColors ? 'text-white/80' : 'text-gray-500'
            }`}
          >
            Home
          </span>
        </Link>
      </div>

      {/* Navigation - Fixed top right, matching home page style */}
      <nav
        className={`fixed top-6 sm:top-8 right-6 sm:right-8 z-50 transition-all duration-300 ${
          scrolled ? 'opacity-100' : 'opacity-100'
        }`}
      >
        <div
          className={`flex flex-col items-end gap-1 sm:gap-2 transition-all duration-300 ${
            scrolled
              ? 'bg-white/90 backdrop-blur-md rounded-xl p-3 sm:p-4 shadow-lg border border-gray-100'
              : ''
          }`}
        >
          {navigation.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`group flex items-center gap-1 sm:gap-2 transition-all duration-300 ${
                link.primary
                  ? `text-sm sm:text-lg font-medium tracking-wide ${
                      isActive(link.path)
                        ? 'text-[#0088cc]'
                        : useLightColors
                          ? 'text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]'
                          : 'text-gray-900'
                    }`
                  : `text-xs sm:text-sm font-medium tracking-wide ${
                      isActive(link.path)
                        ? 'text-[#0088cc]'
                        : useLightColors
                          ? 'text-white/80 drop-shadow-[0_2px_6px_rgba(0,0,0,0.3)]'
                          : 'text-gray-500'
                    }`
              } hover:text-[#0088cc]`}
            >
              <span className="relative">
                {link.name}
                <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-current transition-all duration-300 group-hover:w-full" />
              </span>
              {link.primary && (
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
              )}
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
